import styled, { keyframes } from 'styled-components';

// Animações
const glowPulse = keyframes`
  0% { box-shadow: 0 0 10px rgba(64, 128, 255, 0.4); }
  50% { box-shadow: 0 0 20px rgba(0, 255, 255, 0.7), 0 0 30px rgba(64, 128, 255, 0.5); }
  100% { box-shadow: 0 0 10px rgba(64, 128, 255, 0.4); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const iconPulse = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
`;

const dataFlow = keyframes`
  0% { transform: translateY(-10px); opacity: 0; }
  50% { transform: translateY(0); opacity: 1; }
  100% { transform: translateY(10px); opacity: 0; }
`;

const circleExpand = keyframes`
  0% { transform: scale(0.8); opacity: 0.3; }
  50% { transform: scale(1.2); opacity: 0.7; }
  100% { transform: scale(0.8); opacity: 0.3; }
`;

const scanLine = keyframes`
  0% { transform: translateY(-100%); opacity: 0.5; }
  100% { transform: translateY(100%); opacity: 0; }
`;

const float = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0); }
`;

const techGridPulse = keyframes`
  0% { opacity: 0.1; }
  50% { opacity: 0.3; }
  100% { opacity: 0.1; }
`;

const digitalCircuitFlow = keyframes`
  0% { background-position: 0% 0%; }
  100% { background-position: 100% 100%; }
`;

// Componentes estilizados
export const ChannelsSection = styled.div`
  margin-top: 3rem;
  padding: 2rem;
  position: relative;
  background: rgba(0, 16, 32, 0.7);
  border-radius: 16px;
  border: 1px solid rgba(64, 128, 255, 0.3);
  box-shadow: 0 0 20px rgba(0, 64, 255, 0.2);
  overflow: hidden;
  animation: ${glowPulse} 5s infinite ease-in-out;
  
  /* Efeito de scanner */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 10px;
    background: linear-gradient(to bottom, 
      rgba(0, 255, 255, 0.5), 
      rgba(0, 255, 255, 0)
    );
    animation: ${scanLine} 4s infinite ease-in-out;
    z-index: 1;
  }
  
  /* Background de circuito digital */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 10h10v10H10zM30 10h10v10H30zM50 10h10v10H50zM70 10h10v10H70zM90 10h10v10H90zM10 30h10v10H10zM30 30h10v10H30zM50 30h10v10H50zM70 30h10v10H70zM90 30h10v10H90zM10 50h10v10H10zM30 50h10v10H30zM50 50h10v10H50zM70 50h10v10H70zM90 50h10v10H90zM10 70h10v10H10zM30 70h10v10H30zM50 70h10v10H50zM70 70h10v10H70zM90 70h10v10H90zM10 90h10v10H10zM30 90h10v10H30zM50 90h10v10H50zM70 90h10v10H70zM90 90h10v10H90z' fill='none' stroke='rgba(0, 255, 255, 0.05)' stroke-width='1'/%3E%3Cpath d='M0 20h100M0 40h100M0 60h100M0 80h100M20 0v100M40 0v100M60 0v100M80 0v100' stroke='rgba(0, 255, 255, 0.03)' stroke-width='1'/%3E%3C/svg%3E");
    background-size: 50px 50px;
    opacity: 0.3;
    z-index: -1;
    animation: ${techGridPulse} 4s infinite ease-in-out, ${digitalCircuitFlow} 20s infinite linear;
  }
`;

export const ChannelsTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.8rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 2.5rem;
  color: white;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
  letter-spacing: 2px;
  position: relative;
  
  /* Linha decorativa abaixo do título */
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 180px;
    height: 2px;
    background: linear-gradient(to right, rgba(0, 255, 255, 0), rgba(0, 255, 255, 0.8), rgba(0, 255, 255, 0));
    box-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
  }
`;

export const ChannelsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-top: 1rem;
  position: relative;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 2rem;
  }
  
  /* Linha de conexão entre os canais */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100px;
    height: 2px;
    background: linear-gradient(to right, rgba(0, 255, 255, 0.3), rgba(64, 128, 255, 0.5), rgba(0, 255, 255, 0.3));
    box-shadow: 0 0 10px rgba(0, 255, 255, 0.3);
    
    @media (max-width: 768px) {
      width: 2px;
      height: 80px;
      background: linear-gradient(to bottom, rgba(0, 255, 255, 0.3), rgba(64, 128, 255, 0.5), rgba(0, 255, 255, 0.3));
    }
  }
`;

export const ChannelItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  animation: ${float} 6s infinite ease-in-out;
  animation-delay: ${props => props.delay || '0s'};
  z-index: 2;
`;

export const ChannelIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(0, 32, 64, 0.8), rgba(0, 16, 32, 0.8));
  border: 1px solid rgba(0, 255, 255, 0.5);
  box-shadow: 0 0 15px rgba(0, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
  
  /* Círculos concêntricos */
  &::before, &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    border: 1px dashed rgba(0, 255, 255, 0.3);
    animation: ${circleExpand} 3s infinite;
  }
  
  &::before {
    width: 100px;
    height: 100px;
    animation-delay: 0s;
  }
  
  &::after {
    width: 60px;
    height: 60px;
    animation-delay: 1.5s;
  }
`;

export const ChannelIcon = styled.div`
  font-size: 2.5rem;
  color: rgba(0, 255, 255, 0.9);
  animation: ${iconPulse} 2s infinite ease-in-out;
  filter: drop-shadow(0 0 5px rgba(0, 255, 255, 0.7));
`;

export const ChannelName = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.2rem;
  font-weight: 500;
  margin-top: 1rem;
  color: white;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
  letter-spacing: 1px;
`;

export const DataParticle = styled.div`
  position: absolute;
  width: 3px;
  height: 3px;
  background-color: ${props => props.color || '#00FFFF'};
  border-radius: 50%;
  top: ${props => props.top};
  left: ${props => props.left};
  opacity: 0;
  animation: ${dataFlow} ${props => props.duration || '2s'} infinite;
  animation-delay: ${props => props.delay || '0s'};
  box-shadow: 0 0 3px ${props => props.color || '#00FFFF'};
  z-index: 1;
`;
